import { WaterInvoiceModel } from "@/core/models/water-invoice.model";
import { WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import container from "@/lib/di/container";
import { Alert, Checkbox, ConfigProvider, Form, Input, Modal, message } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);

interface CancelInvoiceForm {
    cancelReason: string;
    confirmCancelPublishedInvoice: boolean;
}

interface CancelInvoiceModalProps {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    data: WaterInvoiceModel;
}

const INPUT_WIDTH = 450;

export const CancelInvoiceModal: React.FC<CancelInvoiceModalProps> = ({
    isModalOpen,
    setIsModalOpen,
    data,
}) => {
    const [form] = Form.useForm<CancelInvoiceForm>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isModalOpen) {
            form.resetFields();
            form.setFieldsValue({ confirmCancelPublishedInvoice: false });
        }
    }, [isModalOpen, data]);

    const handleCancel = () => {
        if (loading) return;
        setIsModalOpen(false);
    };

    const handleSubmit = async () => {
        try {
            const formData = await form.validateFields();
            const cancelReason = (formData.cancelReason ?? "").trim();
            if (!data.id || !cancelReason) {
                message.error("Vui lòng nhập lý do hủy");
                return;
            }

            setLoading(true);
            const result = await waterInvoiceRepo.cancelInvoice({
                id: data.id,
                cancelReason,
                confirmCancelPublishedInvoice: !!formData.confirmCancelPublishedInvoice,
            });
            if (result.success) {
                message.success(result.message);
                setIsModalOpen(false);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal
                open={isModalOpen}
                onCancel={handleCancel}
                width={560}
                style={{ top: 40 }}
                styles={{
                    mask: {
                        backdropFilter: "blur(2px)",
                        backgroundColor: "var(--primary-mask)",
                    },
                }}
                okText="Hủy hóa đơn"
                cancelText="Đóng"
                okButtonProps={{
                    danger: true,
                    loading,
                    disabled: loading,
                }}
                cancelButtonProps={{ disabled: loading }}
                onOk={handleSubmit}
                destroyOnClose
            >
                <div className="flex flex-row w-full items-start justify-start select-none mb-4">
                    <p className="ml-2 text-2xl font-bold text-primary">HỦY HÓA ĐƠN</p>
                </div>

                <Alert
                    type="warning"
                    showIcon
                    className="mb-4"
                    message={`Hủy hóa đơn ${data.month}/${data.year} của khách hàng ${data.waterUser?.code ?? ""} - ${data.waterUser?.name ?? ""}`}
                />

                <Form<CancelInvoiceForm>
                    form={form}
                    layout="vertical"
                    className="px-1"
                    initialValues={{ confirmCancelPublishedInvoice: false }}
                >
                    <Form.Item
                        label="Lý do hủy"
                        name="cancelReason"
                        rules={[
                            { required: true, message: "Vui lòng nhập lý do hủy" },
                            {
                                validator: (_, value) =>
                                    (value ?? "").trim()
                                        ? Promise.resolve()
                                        : Promise.reject(new Error("Vui lòng nhập lý do hủy")),
                            },
                        ]}
                    >
                        <Input.TextArea
                            placeholder="Nhập lý do hủy hóa đơn"
                            style={{ width: INPUT_WIDTH, height: 120, resize: "none" }}
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>
                    <Form.Item
                        name="confirmCancelPublishedInvoice"
                        valuePropName="checked"
                        className="mb-2"
                    >
                        <Checkbox>
                            Tôi xác nhận vẫn hủy hóa đơn dù hóa đơn điện tử đã được phát hành
                        </Checkbox>
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        className="mb-2"
                        message="Lưu ý về hóa đơn điện tử"
                        description="Thao tác này chỉ hủy hóa đơn trong hệ thống nội bộ. Không tự hủy hóa đơn điện tử tại nhà cung cấp (EasyInvoice/MatBao) và không xóa số HĐĐT đã phát hành."
                    />
                </Form>
            </Modal>
        </ConfigProvider>
    );
};
